import { Link } from "react-router-dom";
import {
  Crown,
  Lightning,
  Barbell,
  Sun,
  Sword,
  Anchor,
  Mountains,
  BookOpen,
  TShirt,
  Handshake,
  Megaphone,
} from "@phosphor-icons/react";

const PILLARS = [
  {
    numeral: "I",
    name: "Hope",
    subtitle: "The Spark",
    icon: Sun,
    body:
      "The refusal to accept the end. Whether stripping it from the fallen or fighting for it in the ring, Hope is the vision of a dawn that has not yet come. It is the light we carry into the deepest dark.",
  },
  {
    numeral: "II",
    name: "Courage",
    subtitle: "The Action",
    icon: Sword,
    body:
      "Not the absence of fear, but the savage will to move forward despite it. It is stepping into the ring when you are the underdog, taking the hit, and fighting until the bell rings.",
  },
  {
    numeral: "III",
    name: "Faith",
    subtitle: "The Anchor",
    icon: Anchor,
    body:
      "Unwavering belief in the Most High, the mission, the team, and oneself. It is trust in the Creator's plan. When the world breaks and the ground shakes, Faith is the foundation that holds firm.",
  },
  {
    numeral: "IV",
    name: "Will",
    subtitle: "The Drive",
    icon: Mountains,
    body:
      "The sheer refusal to yield. When talent is not enough and the odds are stacked, Will is the savage endurance that forces a way forward. The power to stand up one more time than you are knocked down.",
  },
];

const WHAT_WE_DO = [
  {
    icon: BookOpen,
    title: "Comics & Manga",
    body: "Original universes, flagship titles, and serialized sagas built to bleed off the page.",
  },
  {
    icon: TShirt,
    title: "Merchandise",
    body: "Wearable mythology — apparel, collectibles, and gear forged for the loyal court.",
  },
  {
    icon: Handshake,
    title: "Collaborations",
    body: "Real-life superstars from WWE, AEW, TNA, Reality of Wrestling and beyond — pulled into the panel.",
  },
  {
    icon: Megaphone,
    title: "Promotions",
    body: "Live events, drops, and behind-the-curtain access to the mine where the heroes are made.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10 min-h-[600px]">
        <div className="absolute inset-0">
          <img
            src="/api/static/images/about-savage-king-portrait.jpg"
            alt="The Savage King in full regalia"
            className="w-full h-full object-cover opacity-80"
            style={{ objectPosition: "75% 25%" }}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            onError={(e) => { e.currentTarget.style.opacity = "0.4"; }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-transparent to-black/80" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-28">
          <div className="section-eyebrow">Savage ComiX Universe · SCU</div>
          <h1 className="font-display text-6xl lg:text-8xl uppercase mt-4 leading-[0.9]">
            Forge Your<br />Legend
          </h1>
          <p className="mt-6 text-lg max-w-xl text-white/85 italic">
            &quot;To forge legends from the raw fire of the human spirit.&quot;
          </p>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-12 gap-10" data-testid="about-who-we-are">
        <div className="lg:col-span-7 space-y-6 text-white/85 leading-relaxed">
          <div className="section-eyebrow">Who We Are</div>
          <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]">
            Not Just A Publisher — <span className="text-[#FF3B30]">A Proving Ground</span>
          </h2>
          <p>
            Savage Comics is a home for stories that bleed, sweat, and fight. We bridge the gap between the raw physicality of professional wrestling and the epic scope of dark fantasy. Our characters are not perfect gods — they are flawed warriors, striving for redemption in a world that tries to break them.
          </p>
          <p>
            We believe that true power is not given — it is earned in the trenches. From the squared circle to the supernatural battlegrounds, our stories are defined by high stakes, hard hits, and the unconquerable human spirit.
          </p>
        </div>
        <div className="lg:col-span-5 grid grid-cols-1 gap-4">
          {[
            { icon: Crown, label: "Founder", val: "The Savage King" },
            { icon: Lightning, label: "First Title", val: "Legacy #001" },
            { icon: Barbell, label: "Built In", val: "The Ring" },
          ].map((b) => (
            <div
              key={b.label}
              className="scu-card p-6 flex items-center gap-5"
              data-testid={`about-stat-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <b.icon size={32} weight="duotone" className="text-[#FF3B30]" />
              <div>
                <div className="text-xs uppercase tracking-widest text-dim">{b.label}</div>
                <div className="font-display text-2xl uppercase">{b.val}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FOUR PILLARS */}
      <section className="border-t border-white/10 bg-gradient-to-b from-black via-[#0a0a0a] to-black py-20" data-testid="about-pillars">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="section-eyebrow justify-center">Our Foundation</div>
            <h2 className="font-display text-4xl sm:text-5xl uppercase mt-3">
              Four <span className="text-[#FF3B30]">Unshakeable</span> Pillars
            </h2>
            <p className="text-dim mt-4">
              The traits that define our heroes, our stories, and our company.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-2 gap-6">
            {PILLARS.map((p) => (
              <article
                key={p.name}
                className="scu-card p-8 relative overflow-hidden group hover:border-[#FF3B30] transition-colors"
                data-testid={`pillar-${p.name.toLowerCase()}`}
              >
                <div className="absolute -right-4 -top-6 font-display text-[10rem] leading-none text-white/5 select-none">
                  {p.numeral}
                </div>
                <div className="relative">
                  <p.icon size={42} weight="duotone" className="text-[#FF3B30]" />
                  <div className="mt-4 flex items-baseline gap-3">
                    <span className="font-display text-xs uppercase tracking-widest text-dim">
                      Pillar {p.numeral}
                    </span>
                    <span className="font-display text-xs uppercase tracking-widest text-white/40">
                      {p.subtitle}
                    </span>
                  </div>
                  <h3 className="font-display text-4xl uppercase mt-1">{p.name}</h3>
                  <p className="mt-4 text-white/80 leading-relaxed">{p.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="border-t border-white/10 py-20" data-testid="about-what-we-do">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="section-eyebrow">What We Do</div>
              <h2 className="font-display text-4xl sm:text-5xl uppercase mt-3 leading-[0.95]">
                Comics. Gear.<br />
                <span className="text-[#FF3B30]">Real Superstars.</span>
              </h2>
              <p className="text-white/80 mt-6 leading-relaxed">
                Our faith in the Most High and our unyielding drive to spread the good news fuel everything we build — new characters, new stories, and the real-world element of professional wrestlers from WWE, AEW, TNA, Reality of Wrestling and beyond.
              </p>
              <p className="text-white/80 mt-4 leading-relaxed">
                Step inside the mine. Meet the real-life superheroes. Watch the legend get forged in real time.
              </p>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {WHAT_WE_DO.map((w) => (
                <div
                  key={w.title}
                  className="scu-card p-6 hover:border-[#FF3B30] transition-colors"
                  data-testid={`do-${w.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <w.icon size={32} weight="duotone" className="text-[#FF3B30]" />
                  <h3 className="font-display text-2xl uppercase mt-3">{w.title}</h3>
                  <p className="text-dim text-sm mt-2 leading-relaxed">{w.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* THE FOUNDER */}
      <section className="border-t border-white/10 bg-[#0a0a0a] py-20" data-testid="about-founder">
        <div className="max-w-5xl mx-auto px-6">
          <div className="section-eyebrow justify-center text-center">The Founder</div>
          <h2 className="font-display text-4xl sm:text-5xl uppercase mt-3 text-center">
            From The Ring <span className="text-[#FF3B30]">To The Page</span>
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6 text-center">
            <div className="scu-card p-8">
              <div className="font-display text-6xl text-[#FF3B30]">10+</div>
              <div className="text-xs uppercase tracking-widest text-dim mt-2">Years In The Wrestling Business</div>
            </div>
            <div className="scu-card p-8">
              <div className="font-display text-6xl text-[#FF3B30]">001</div>
              <div className="text-xs uppercase tracking-widest text-dim mt-2">First Issue Of The Legacy</div>
            </div>
            <div className="scu-card p-8">
              <div className="font-display text-6xl text-[#FF3B30]">∞</div>
              <div className="text-xs uppercase tracking-widest text-dim mt-2">Stories Yet To Be Told</div>
            </div>
          </div>
          <p className="text-white/85 leading-relaxed mt-10 max-w-3xl mx-auto text-center text-lg">
            Over ten years in the wrestling business — and excitingly new to the comic book industry. SCU was started out of a drive and a desire to share what&apos;s inside of all of us:
            <span className="text-[#FF3B30] font-display uppercase block mt-3 text-2xl">our story.</span>
          </p>
        </div>
      </section>

      {/* SPONSORS / PARTNERS */}
      <section className="border-t border-white/10 py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="section-eyebrow justify-center">Sponsors · Press · Partners</div>
          <h2 className="font-display text-4xl sm:text-5xl uppercase mt-3">Partner With The Legacy At Ground Level</h2>
          <p className="text-dim mt-4 max-w-xl mx-auto">
            Savage King: Legacy is the debut of a high-impact comic series — epic storytelling, grounded mythology, cinematic action. Now seeking sponsors and brand endorsements.
          </p>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <a
              href="mailto:Info@savagecomixuniverse.com"
              className="scu-card p-5 hover:border-[#FF3B30] transition-colors"
              data-testid="about-contact-email"
            >
              <div className="text-xs uppercase tracking-widest text-dim">Contact</div>
              <div className="font-display text-base mt-1 break-all">Info@savagecomixuniverse.com</div>
            </a>
            <a
              href="https://linktr.ee/thesavageking"
              target="_blank"
              rel="noopener noreferrer"
              className="scu-card p-5 hover:border-[#FF3B30] transition-colors"
              data-testid="about-contact-social"
            >
              <div className="text-xs uppercase tracking-widest text-dim">Social</div>
              <div className="font-display text-base mt-1">linktr.ee/thesavageking</div>
            </a>
          </div>
          <Link to="/comics" className="btn-red inline-block mt-10" data-testid="about-cta">
            Read The Comic
          </Link>
        </div>
      </section>
    </div>
  );
}
